// Shared QSL design renderer — used by the Design Studio (live preview) and by
// the send/queue flow (renders the saved layout_json to a canvas → JPEG).
// Card canvas is always 900x540.

export var CARD_W = 900;
export var CARD_H = 540;

// QSO fields a user can bind to a text element in the studio.
export var FIELD_DEFS = [
  { key: "my_callsign", label: "My Callsign", sample: "PY2XYZ" },
  { key: "to_callsign", label: "To Callsign", sample: "DL1ABC" },
  { key: "date",        label: "Date",        sample: "20260115" },
  { key: "time",        label: "Time (UTC)",  sample: "1432" },
  { key: "band",        label: "Band",        sample: "20m" },
  { key: "mode",        label: "Mode",        sample: "SSB" },
  { key: "freq",        label: "Frequency",   sample: "14.250" },
  { key: "rst_sent",    label: "RST Sent",    sample: "59" },
  { key: "rst_rcvd",    label: "RST Rcvd",    sample: "59" },
  { key: "grid",        label: "Their Grid",  sample: "JO31" },
  { key: "my_grid",     label: "My Grid",     sample: "GG66" },
  { key: "message",     label: "Message",     sample: "Thanks for the QSO! 73" }
];

export var SAMPLE_DATA = {
  my_callsign: "PY2XYZ",
  to_callsign: "DL1ABC",
  date: "20260115",
  time: "1432",
  band: "20m",
  mode: "SSB",
  freq: "14.250",
  rst_sent: "59",
  rst_rcvd: "59",
  grid: "JO31",
  my_grid: "GG66",
  message: "Thanks for the QSO! 73"
};

// Format a bound field's raw value for display.
export function formatField(field, data) {
  var v = data && data[field] != null ? String(data[field]) : "";
  if (field === "date" && v.length >= 8) {
    return v.substring(0, 4) + "/" + v.substring(4, 6) + "/" + v.substring(6, 8);
  }
  if (field === "time" && v.length >= 4) {
    return v.substring(0, 2) + ":" + v.substring(2, 4) + " UTC";
  }
  if (field === "freq" && v) return v + " MHz";
  return v;
}

// Resolve the text an element renders (field-bound or literal).
function elementText(el, data) {
  if (el.field) {
    var val = formatField(el.field, data);
    if (!val) val = el.placeholder || "";
    return (el.prefix || "") + val + (el.suffix || "");
  }
  return el.text || "";
}

function roundRectPath(ctx, x, y, w, h, r) {
  if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return; }
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Draw a full design. `images` maps a src string → loaded Image (HTMLImageElement
// or node-canvas Image). Missing images are skipped gracefully.
export function drawDesign(ctx, layout, data, images) {
  images = images || {};
  var w = (layout && layout.w) || CARD_W;
  var h = (layout && layout.h) || CARD_H;
  ctx.clearRect(0, 0, w, h);

  // ── Background ──
  var bg = (layout && layout.bg) || { type: "solid", color: "#0c1133" };
  if (bg.type === "image" && bg.src && images[bg.src]) {
    var img = images[bg.src];
    // cover fit
    var ir = img.width / img.height, cr = w / h, dw, dh, dx, dy;
    if (ir > cr) { dh = h; dw = h * ir; dx = (w - dw) / 2; dy = 0; }
    else { dw = w; dh = w / ir; dx = 0; dy = (h - dh) / 2; }
    ctx.drawImage(img, dx, dy, dw, dh);
    if (bg.overlay) {
      ctx.fillStyle = bg.overlay;
      ctx.fillRect(0, 0, w, h);
    }
  } else if (bg.type === "linear") {
    var ang = (bg.angle || 0) * Math.PI / 180;
    var x2 = Math.cos(ang) * w, y2 = Math.sin(ang) * h;
    var g = ctx.createLinearGradient(0, 0, x2, y2);
    g.addColorStop(0, bg.c1 || "#0c1133");
    g.addColorStop(1, bg.c2 || "#1b2356");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.fillStyle = bg.color || "#0c1133";
    ctx.fillRect(0, 0, w, h);
  }

  // ── Elements ──
  var els = (layout && layout.elements) || [];
  for (var i = 0; i < els.length; i++) {
    var el = els[i];
    ctx.save();
    ctx.globalAlpha = el.opacity != null ? el.opacity : 1;

    if (el.type === "rect") {
      var rr = el.radius || 0;
      if (el.fill) {
        ctx.fillStyle = el.fill;
        if (rr > 0) { roundRectPath(ctx, el.x, el.y, el.w, el.h, rr); ctx.fill(); }
        else ctx.fillRect(el.x, el.y, el.w, el.h);
      }
      if (el.stroke) {
        ctx.strokeStyle = el.stroke;
        ctx.lineWidth = el.sw || 1;
        if (rr > 0) { roundRectPath(ctx, el.x, el.y, el.w, el.h, rr); ctx.stroke(); }
        else ctx.strokeRect(el.x, el.y, el.w, el.h);
      }
    } else if (el.type === "line") {
      ctx.strokeStyle = el.color || "#ffffff";
      ctx.lineWidth = el.sw || 2;
      ctx.beginPath();
      ctx.moveTo(el.x, el.y);
      ctx.lineTo(el.x2, el.y2);
      ctx.stroke();
    } else if (el.type === "image") {
      if (el.src && images[el.src]) {
        if (el.radius) {
          roundRectPath(ctx, el.x, el.y, el.w, el.h, el.radius);
          ctx.clip();
        }
        ctx.drawImage(images[el.src], el.x, el.y, el.w, el.h);
      }
    } else {
      // text / field
      var txt = elementText(el, data);
      if (txt !== "") {
        // optional small caption/label above the value (clear gap, no overlap)
        if (el.label) {
          var sz = el.size || 20;
          ctx.fillStyle = el.labelColor || "#9aa4d4";
          ctx.font = "bold " + Math.max(9, Math.round(sz * 0.4)) + "px " + (el.font || "Arial");
          ctx.textAlign = el.align || "left";
          ctx.fillText(el.label, el.x, el.y - sz * 0.72 - 6);
        }
        if (el.shadow) {
          ctx.shadowColor = el.shadowColor || el.color || "#000";
          ctx.shadowBlur = el.shadow;
        }
        ctx.fillStyle = el.color || "#ffffff";
        var weight = el.weight || "normal";
        var style = el.italic ? "italic " : "";
        ctx.font = style + weight + " " + (el.size || 20) + "px " + (el.font || "Arial");
        ctx.textAlign = el.align || "left";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(txt, el.x, el.y);
      }
    }
    ctx.restore();
  }
}

// Collect all image src strings referenced by a layout (bg + image elements).
export function collectImageSrcs(layout) {
  var srcs = [];
  if (layout && layout.bg && layout.bg.type === "image" && layout.bg.src) srcs.push(layout.bg.src);
  var els = (layout && layout.elements) || [];
  for (var i = 0; i < els.length; i++) {
    if (els[i].type === "image" && els[i].src) srcs.push(els[i].src);
  }
  return srcs;
}

// Starter presets in HamExpert identity. All use a clean 4-column QSO grid
// (panel x55..845) with both rows on the same column centers so nothing staggers.
// Column centers: 160 / 353 / 547 / 740.
export function defaultPresets() {
  return [
    {
      name: "Deep Space",
      layout: {
        v: 1, w: CARD_W, h: CARD_H,
        bg: { type: "linear", c1: "#0a0e2e", c2: "#1b2356", angle: 40 },
        elements: [
          { id: "bar", type: "rect", x: 0, y: 0, w: 900, h: 6, fill: "#3fcfd5" },
          { id: "bar2", type: "rect", x: 0, y: 6, w: 900, h: 2, fill: "#c0f021", opacity: 0.7 },
          { id: "my", type: "field", field: "my_callsign", x: 450, y: 112, size: 62, color: "#3fcfd5", weight: "bold", align: "center", shadow: 22, shadowColor: "#3fcfd5" },
          { id: "ul", type: "line", x: 330, y: 130, x2: 570, y2: 130, color: "#c0f021", sw: 2, opacity: 0.8 },
          { id: "sub", type: "text", text: "CONFIRMING QSO WITH", x: 450, y: 168, size: 14, color: "#9aa4d4", align: "center", weight: "bold" },
          { id: "to", type: "field", field: "to_callsign", x: 450, y: 222, size: 46, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "panel", type: "rect", x: 55, y: 262, w: 790, h: 176, fill: "rgba(63,207,213,0.07)", stroke: "#34407e", sw: 1, radius: 16 },
          { id: "div", type: "line", x: 90, y: 350, x2: 810, y2: 350, color: "#34407e", sw: 1, opacity: 0.6 },
          { id: "f1", type: "field", field: "date", label: "DATE", x: 160, y: 322, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "f2", type: "field", field: "time", label: "TIME", x: 353, y: 322, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "f3", type: "field", field: "band", label: "BAND", x: 547, y: 322, size: 24, color: "#c0f021", weight: "bold", align: "center" },
          { id: "f4", type: "field", field: "mode", label: "MODE", x: 740, y: 322, size: 24, color: "#c0f021", weight: "bold", align: "center" },
          { id: "f5", type: "field", field: "rst_sent", label: "RST TX", x: 160, y: 410, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "f6", type: "field", field: "rst_rcvd", label: "RST RX", x: 353, y: 410, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "f7", type: "field", field: "freq", label: "FREQ", x: 547, y: 410, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "f8", type: "field", field: "grid", label: "GRID", x: 740, y: 410, size: 24, color: "#eef1ff", weight: "bold", align: "center" },
          { id: "msg", type: "field", field: "message", x: 450, y: 478, size: 16, color: "#9aa4d4", align: "center", italic: true },
          { id: "fbar", type: "rect", x: 0, y: 512, w: 900, h: 28, fill: "rgba(0,0,0,0.35)" },
          { id: "ft", type: "text", text: "HamExpert.cloud  \u2022  eQSL  \u2022  73!", x: 450, y: 530, size: 12, color: "#7c80f0", align: "center", weight: "bold" }
        ]
      }
    },
    {
      name: "Aurora Glow",
      layout: {
        v: 1, w: CARD_W, h: CARD_H,
        bg: { type: "linear", c1: "#04122a", c2: "#0a2a3a", angle: 60 },
        elements: [
          { id: "glow", type: "rect", x: 0, y: 0, w: 900, h: 240, fill: "rgba(0,255,163,0.06)" },
          { id: "side", type: "rect", x: 0, y: 0, w: 10, h: 540, fill: "#00ffa3" },
          { id: "my", type: "field", field: "my_callsign", x: 450, y: 120, size: 64, color: "#00ffa3", weight: "bold", align: "center", shadow: 26, shadowColor: "#00ffa3" },
          { id: "sub", type: "text", text: "confirming QSO with", x: 450, y: 162, size: 15, color: "#a9e7d6", align: "center", italic: true },
          { id: "to", type: "field", field: "to_callsign", x: 450, y: 222, size: 46, color: "#ffffff", weight: "bold", align: "center" },
          { id: "panel", type: "rect", x: 55, y: 262, w: 790, h: 176, fill: "rgba(0,255,163,0.05)", stroke: "rgba(0,255,163,0.25)", sw: 1, radius: 16 },
          { id: "div", type: "line", x: 90, y: 350, x2: 810, y2: 350, color: "rgba(0,255,163,0.2)", sw: 1 },
          { id: "f1", type: "field", field: "date", label: "DATE", x: 160, y: 322, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f2", type: "field", field: "time", label: "TIME", x: 353, y: 322, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f3", type: "field", field: "band", label: "BAND", x: 547, y: 322, size: 24, color: "#00ffa3", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f4", type: "field", field: "mode", label: "MODE", x: 740, y: 322, size: 24, color: "#00ffa3", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f5", type: "field", field: "rst_sent", label: "RST TX", x: 160, y: 410, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f6", type: "field", field: "rst_rcvd", label: "RST RX", x: 353, y: 410, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f7", type: "field", field: "freq", label: "FREQ", x: 547, y: 410, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "f8", type: "field", field: "grid", label: "GRID", x: 740, y: 410, size: 24, color: "#d0fff0", weight: "bold", align: "center", labelColor: "#5fbfa3" },
          { id: "msg", type: "field", field: "message", x: 450, y: 478, size: 16, color: "#a9e7d6", align: "center", italic: true },
          { id: "ft", type: "text", text: "HamExpert.cloud  \u2022  73!", x: 450, y: 522, size: 12, color: "rgba(0,255,163,0.55)", align: "center" }
        ]
      }
    },
    {
      name: "Clean Light",
      layout: {
        v: 1, w: CARD_W, h: CARD_H,
        bg: { type: "solid", color: "#f4f6fb" },
        elements: [
          { id: "head", type: "rect", x: 0, y: 0, w: 900, h: 130, fill: "#0c1133" },
          { id: "accent", type: "rect", x: 0, y: 130, w: 900, h: 5, fill: "#3fcfd5" },
          { id: "my", type: "field", field: "my_callsign", x: 45, y: 85, size: 54, color: "#3fcfd5", weight: "bold", align: "left" },
          { id: "tag", type: "text", text: "QSL CONFIRMED", x: 855, y: 78, size: 20, color: "#c0f021", weight: "bold", align: "right" },
          { id: "lbl", type: "text", text: "TO RADIO STATION", x: 45, y: 185, size: 13, color: "#9aa4d4", weight: "bold", align: "left" },
          { id: "to", type: "field", field: "to_callsign", x: 45, y: 232, size: 46, color: "#0c1133", weight: "bold", align: "left" },
          { id: "panel", type: "rect", x: 45, y: 268, w: 810, h: 180, fill: "#ffffff", stroke: "#d7ddf0", sw: 1, radius: 12 },
          { id: "div", type: "line", x: 75, y: 358, x2: 825, y2: 358, color: "#e4e9f5", sw: 1 },
          { id: "f1", type: "field", field: "date", label: "DATE", x: 165, y: 330, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f2", type: "field", field: "time", label: "TIME", x: 355, y: 330, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f3", type: "field", field: "band", label: "BAND", x: 545, y: 330, size: 22, color: "#1b2356", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f4", type: "field", field: "mode", label: "MODE", x: 735, y: 330, size: 22, color: "#1b2356", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f5", type: "field", field: "rst_sent", label: "RST TX", x: 165, y: 420, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f6", type: "field", field: "rst_rcvd", label: "RST RX", x: 355, y: 420, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f7", type: "field", field: "freq", label: "FREQ", x: 545, y: 420, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "f8", type: "field", field: "grid", label: "GRID", x: 735, y: 420, size: 22, color: "#0c1133", weight: "bold", align: "center", labelColor: "#9aa4d4" },
          { id: "msg", type: "field", field: "message", x: 45, y: 492, size: 15, color: "#5a648f", align: "left", italic: true },
          { id: "ft", type: "text", text: "HamExpert.cloud", x: 855, y: 492, size: 13, color: "#9aa4d4", align: "right", weight: "bold" }
        ]
      }
    }
  ];
}
